import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Box,
    Button,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Stack,
    Text,
} from '@chakra-ui/react';
import { createColumnHelper, getCoreRowModel, getSortedRowModel, Row, useReactTable } from '@tanstack/react-table';
import { groupBy, reduce, uniq } from 'lodash';
import { useCallback, useMemo } from 'react';

import { PortfolioBase } from '../_openapi/portfolio-optimizer';
import { useSubmitManualTrades } from '../api/trade';
import { baseCol, expanderCol, moneyCol, numberCol, textCol } from '../widgets/Table/columnHelpers/colDef';
import { MoneyCell } from '../widgets/Table/MoneyCell';
import { NumberCell } from '../widgets/Table/NumberCell';
import { Table } from '../widgets/Table/Table';
import { TradeReasonSelector, useTradeReasonState } from '../widgets/TradeReasonSelector';
import { TradeSideTag } from '../widgets/TradeSideTag';
import { ManualTrade } from './TradeBlotter';
import { useTradeBlotterContext } from './TradeBlotterContext';
import { tradeCalculator } from './tradeCalculator';

export const ManualTradeReviewModal = ({
    trades,
    onClose,
    isOpen,
}: {
    trades: ManualTrade[];
    isOpen: boolean;
    onClose: () => void;
}) => {
    const { mutate, isPending } = useSubmitManualTrades();
    const { clearTrades } = useTradeBlotterContext();
    const { reason, ...tradeReasonState } = useTradeReasonState();

    const onClick = useCallback(() => {
        mutate(
            { reason, trades },
            {
                onSuccess: () => {
                    onClose();
                    clearTrades();
                },
            },
        );
    }, [clearTrades, mutate, onClose, reason, trades]);

    const zeroTradeValueTrades = useMemo(() => {
        return uniq(
            trades
                .filter((trade) => !tradeCalculator.getTradeValue(trade))
                .map((trade) => trade.portfolio?.portfolioGuid),
        );
    }, [trades]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Review trades before submitting</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Summary trades={trades} />

                    <TradeReasonSelector {...tradeReasonState} />

                    {zeroTradeValueTrades.length > 0 && (
                        <Alert status="warning" mt={4} borderRadius="md">
                            <AlertIcon />
                            <Stack gap={0}>
                                <AlertTitle>Zero trade value for portfolio(s):</AlertTitle>
                                <AlertDescription display="flex" flexDir="column">
                                    {zeroTradeValueTrades.map((guid) => (
                                        <Text key={guid} fontSize="sm">
                                            {guid}
                                        </Text>
                                    ))}
                                </AlertDescription>
                            </Stack>
                        </Alert>
                    )}
                </ModalBody>

                <ModalFooter justifyContent="space-between">
                    <Button variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button colorScheme="teal" isLoading={isPending} onClick={onClick} isDisabled={!reason.length}>
                        Good to send!
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

interface SummaryEntry {
    portfolio: PortfolioBase;
    trades: ManualTrade[];
}

const summaryColumnHelper = createColumnHelper<SummaryEntry>();
const Summary = ({ trades }: { trades: ManualTrade[] }) => {
    const data = useMemo(() => {
        return reduce(
            groupBy(trades, 'portfolio.pId'),
            (result, value: ManualTrade[]) => {
                return [
                    ...result,
                    {
                        portfolio: value[0].portfolio!,
                        trades: value,
                    },
                ];
            },
            [] as SummaryEntry[],
        );
    }, [trades]);

    const table = useReactTable({
        defaultColumn: {
            enableColumnFilter: false,
        },
        data,
        initialState: {
            expanded: true,
        },
        columns: [
            summaryColumnHelper.display(expanderCol()),
            summaryColumnHelper.accessor('portfolio.portfolioGuid', textCol({ id: 'portfolioGuid' })),
            summaryColumnHelper.accessor('portfolio.aC', textCol({ id: 'aC' })),
            summaryColumnHelper.display({
                ...baseCol({ id: 'totalQuantity' }),
                cell: ({ row }) => {
                    const totalQuantity = row.original.trades.reduce(
                        (result, trade) => result + Number(trade.quantity),
                        0,
                    );
                    return <NumberCell value={totalQuantity} />;
                },
            }),
            summaryColumnHelper.display({
                ...baseCol({ id: 'totalValue' }),
                cell: ({ row }) => {
                    const totalValue = row.original.trades.reduce(
                        (result, trade) => result + (tradeCalculator.getTradeValue(trade) ?? 0),
                        0,
                    );
                    return <MoneyCell value={totalValue} />;
                },
            }),
            summaryColumnHelper.accessor('portfolio.nav', moneyCol({ id: 'nav' })),
            summaryColumnHelper.accessor('portfolio.cash', moneyCol({ id: 'cash' })),
        ],
        getRowCanExpand: (row) => row.original.trades.length > 0,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        sortDescFirst: false,
    });

    return <Table table={table} SubComponent={PortfolioTrades} allowXOverflow />;
};

const tradesColumnHelper = createColumnHelper<ManualTrade>();
const PortfolioTrades = ({ row }: { row: Row<SummaryEntry> }) => {
    const table = useReactTable({
        defaultColumn: {
            enableColumnFilter: false,
        },
        data: row.original.trades,
        initialState: {
            expanded: true,
        },
        columns: [
            tradesColumnHelper.accessor('side', {
                ...baseCol({ id: 'side' }),
                cell: ({ getValue }) => <TradeSideTag side={getValue()} />,
            }),
            tradesColumnHelper.accessor('asset.symbol', textCol({ id: 'symbol' })),
            tradesColumnHelper.accessor('asset.description', textCol({ id: 'description' })),
            tradesColumnHelper.accessor('quantity', numberCol({ id: 'quantity' })),
            tradesColumnHelper.accessor('orderType', textCol({ id: 'orderType' })),
            tradesColumnHelper.display({
                ...baseCol({ id: 'price' }),
                cell: (cell) => <MoneyCell value={tradeCalculator.getAdjustedCleanPrice(cell.row.original)} />,
            }),
            tradesColumnHelper.display({
                ...baseCol({ id: 'tradeValue' }),
                cell: (cell) => <MoneyCell value={tradeCalculator.getTradeValue(cell.row.original)} />,
            }),
        ],
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        sortDescFirst: false,
    });

    return (
        <Box>
            <Table table={table} allowXOverflow />
        </Box>
    );
};
